import { ChatMessage } from '@/models/Messenger';
import { MessageWindow, mergeNewestPage, prependOlderPage } from '@/state/messenger/messageWindow';
import { logError } from '@/utils/logger';
import { Ref, ref } from 'vue';

export interface UseMessageWindowOptions {
  // Window contents, owned by the caller (previews/selection read it there).
  messages: Ref<ChatMessage[]>;
  pageSize: number;
  fetchPage: (chatId: string, params: { beforeMessageId?: string; limit: number }) => Promise<MessageWindow>;
  currentChatId: () => string;
  exists: (chatId: string) => boolean;
  // Runs after open/refresh applied the window (unread/read handling).
  onApplied: (chatId: string) => Promise<void> | void;
}

// Cursor-paged window orchestration over one chat's messages; reusable per
// chat type by injecting that type's fetchPage.
const useMessageWindow = ({
  messages, pageSize, fetchPage, currentChatId, exists, onApplied
}: UseMessageWindowOptions) => {
  const hasMore = ref(false);
  const isLoadingOlder = ref(false);
  // Chat the window content currently belongs to (empty until first apply).
  const appliedChatId = ref('');

  const clear = () => {
    messages.value = [];
    hasMore.value = false;
    appliedChatId.value = '';
  };

  const open = async (chatId: string) => {
    if (!exists(chatId)) {
      clear();
      return false;
    }
    // Block load-older with a stale cursor while the switch is in flight.
    hasMore.value = false;
    const page = await fetchPage(chatId, { limit: pageSize });
    // The user may have switched chats while the request was in flight.
    if (currentChatId() !== chatId) return false;
    messages.value = page.messages;
    hasMore.value = page.hasMore;
    appliedChatId.value = chatId;
    await onApplied(chatId);
    return true;
  };

  // Poll/refresh path: merge so loaded older pages survive. Returns whether
  // the window was applied — callers must not derive previews otherwise.
  const refreshNewest = async (chatId: string) => {
    if (!exists(chatId)) {
      clear();
      return false;
    }
    const page = await fetchPage(chatId, { limit: pageSize });
    if (currentChatId() !== chatId) return false;
    const window = mergeNewestPage({ messages: messages.value, hasMore: hasMore.value }, page);
    messages.value = window.messages;
    hasMore.value = window.hasMore;
    appliedChatId.value = chatId;
    await onApplied(chatId);
    return true;
  };

  const loadOlder = async () => {
    const chatId = currentChatId();
    const [oldest] = messages.value;
    if (!chatId || !oldest || !hasMore.value || isLoadingOlder.value) return;
    isLoadingOlder.value = true;
    try {
      const page = await fetchPage(chatId, { beforeMessageId: oldest.messageId, limit: pageSize });
      if (currentChatId() !== chatId) return;
      // Discard if the window was replaced meanwhile (re-open or gap reset).
      if (messages.value[0]?.messageId !== oldest.messageId) return;
      const window = prependOlderPage({ messages: messages.value, hasMore: hasMore.value }, page);
      messages.value = window.messages;
      hasMore.value = window.hasMore;
    } catch (error) {
      // 404 = cursor no longer in the chat: reset to the newest page.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 && currentChatId() === chatId) await open(chatId);
      else logError(error);
    } finally {
      isLoadingOlder.value = false;
    }
  };

  return {
    hasMore, isLoadingOlder, appliedChatId, open, refreshNewest, loadOlder
  };
};

export default useMessageWindow;
